import { FC, useMemo } from "react";

import type { IMultiAnalysisTimeRange } from "../../types";
import { getFramePlotRatios } from "../../utils/range-frame";

const FRAME_COLOR = "#3f6db5";

interface IRangeSelectorFrameProps {
  globalRange: IMultiAnalysisTimeRange;
  frameRange: IMultiAnalysisTimeRange;
  plotLeft: number;
  plotTop: number;
  plotWidth: number;
  plotHeight: number;
  onPointerDownMove: (event: React.PointerEvent) => void;
  onPointerDownResizeLeft: (event: React.PointerEvent) => void;
  onPointerDownResizeRight: (event: React.PointerEvent) => void;
  onDoubleClickReset?: () => void;
}

export const RangeSelectorFrame: FC<IRangeSelectorFrameProps> = ({
  globalRange,
  frameRange,
  plotLeft,
  plotTop,
  plotWidth,
  plotHeight,
  onPointerDownMove,
  onPointerDownResizeLeft,
  onPointerDownResizeRight,
  onDoubleClickReset,
}) => {
  const frameRect = useMemo(() => {
    const { leftRatio, rightRatio } = getFramePlotRatios(frameRange, globalRange);

    return {
      x: plotLeft + leftRatio * plotWidth,
      width: Math.max(8, (rightRatio - leftRatio) * plotWidth),
    };
  }, [frameRange, globalRange, plotLeft, plotWidth]);

  const topHandleHeight = 8;
  const sideHandleHeight = 22;
  const sideArrowDepth = 7;
  const topHandleWidth = 36;
  const sideHandleCenterY = plotTop + plotHeight / 2;
  const sideHandleHalfHeight = sideHandleHeight / 2;
  const leftHandleX = frameRect.x;
  const rightHandleX = frameRect.x + frameRect.width;

  const leftArrowPoints = `${leftHandleX - sideArrowDepth},${sideHandleCenterY} ${leftHandleX},${
    sideHandleCenterY - sideHandleHalfHeight
  } ${leftHandleX},${sideHandleCenterY + sideHandleHalfHeight}`;
  const rightArrowPoints = `${rightHandleX + sideArrowDepth},${sideHandleCenterY} ${rightHandleX},${
    sideHandleCenterY - sideHandleHalfHeight
  } ${rightHandleX},${sideHandleCenterY + sideHandleHalfHeight}`;

  return (
    <g>
      <rect
        x={frameRect.x}
        y={plotTop}
        width={frameRect.width}
        height={plotHeight}
        fill="none"
        stroke={FRAME_COLOR}
        strokeWidth={1}
        pointerEvents="none"
      />
      <polygon
        points={leftArrowPoints}
        fill={FRAME_COLOR}
        style={{ cursor: "ew-resize" }}
        onPointerDown={onPointerDownResizeLeft}
      />
      <polygon
        points={rightArrowPoints}
        fill={FRAME_COLOR}
        style={{ cursor: "ew-resize" }}
        onPointerDown={onPointerDownResizeRight}
      />
      <rect
        x={frameRect.x + frameRect.width / 2 - topHandleWidth / 2}
        y={plotTop - topHandleHeight}
        width={topHandleWidth}
        height={topHandleHeight}
        fill={FRAME_COLOR}
        style={{ cursor: "grab" }}
        onPointerDown={onPointerDownMove}
        onDoubleClick={event => {
          event.stopPropagation();
          onDoubleClickReset?.();
        }}
      />
    </g>
  );
};
